import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { FC, ReactNode } from 'react';

/**
 * As peças de uma tela de detalhe.
 *
 * Existem para que os campos parem de ser renderizados todos com o mesmo peso: uma seção tem
 * título, um rótulo é menor e mais apagado que o valor que ele descreve, e um par
 * rótulo/valor cabe numa linha em vez de duas.
 */

export const Section: FC<{
  title: string;
  caption?: string;
  action?: ReactNode;
  children: ReactNode;
}> = ({ title, caption, action, children }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Box py={4}>
      <Flex align="baseline" justify="space-between" gap={3} mb={3} flexWrap="wrap">
        <Flex align="baseline" gap={3} flexWrap="wrap">
          <Text fontSize="md" fontWeight="semibold">
            {title}
          </Text>
          {caption && (
            <Text fontSize="sm" color={muted}>
              {caption}
            </Text>
          )}
        </Flex>
        {action}
      </Flex>
      {children}
    </Box>
  );
};

export const Field: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Box minW={0}>
      <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="wide">
        {title}
      </Text>
      <Box fontSize="md">{children}</Box>
    </Box>
  );
};

/** Par rótulo/valor numa linha só — metade da altura dos pares empilhados. */
export const DefinitionRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Flex justify="space-between" align="baseline" gap={4} py={1}>
      <Text fontSize="sm" color={muted} whiteSpace="nowrap">
        {label}
      </Text>
      <Box fontSize="sm" fontWeight="medium" textAlign="right" minW={0}>
        {children}
      </Box>
    </Flex>
  );
};

export const InfoPanel: FC<{ title?: string; action?: ReactNode; children: ReactNode }> = ({
  title,
  action,
  children,
}) => {
  const bg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={4}>
      {(title || action) && (
        <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
          {title && (
            <Text fontWeight="semibold" fontSize="sm">
              {title}
            </Text>
          )}
          {action}
        </Flex>
      )}
      {children}
    </Box>
  );
};
